import React from 'react';
import { Table } from '../../../components/ui/DataDisplay';
import { TableColumn } from '../../../components/ui/DataDisplay';
import { ReviewCostingType } from '../../../services/pAndLApi';

/**
 * `review_cost_categories.name` — the backend's costing-type taxonomy.
 *
 * `getReviewCostingType` only returns the category NAME (`rcc.name as reviewCategoryName`),
 * not `review_costing_type.reviewCategoryTypeId`, so the grouping below has to key off the
 * name. The ids are kept in the comments so the mapping is auditable:
 *   1 Variable Cost · 2 Indirect Expenses · 3 Est. OPD Count · 6 Est. Revenue · 7 Revenue
 * If the API ever returns the id, switch these lookups to it — it is the stable key.
 */
const COST_CATEGORY_NAMES = ['Variable Cost', 'Indirect Expenses']; // ids 1, 2 — real money out
const COUNT_CATEGORY_NAMES = ['Est. OPD Count']; // id 3 — a headcount, not money

type RowKind = 'cost' | 'revenue' | 'count' | 'total' | 'section';

const rowKindFor = (reviewCategoryName: string): RowKind => {
	if (COST_CATEGORY_NAMES.includes(reviewCategoryName)) return 'cost';
	if (COUNT_CATEGORY_NAMES.includes(reviewCategoryName)) return 'count';
	// Everything left is money the facility takes IN (ids 6, 7), never part of a cost total.
	return 'revenue';
};

interface CostingBudgetRow extends Record<string, unknown> {
	id: number;
	costingType: string;
	budget: string;
	reviewCategoryName: string;
	kind: RowKind;
}

interface CostingBudgetTableProps {
	costingTypes: ReviewCostingType[];
	budgets: Record<number, string>; // Map of costing type ID to budget value
	onBudgetChange: (costingTypeId: number, value: string) => void;
	isLoading?: boolean;
}

// Negative ids so the pseudo-rows can never collide with a real costing type id.
const TOTAL_ROW_ID = -1;
const REVENUE_SECTION_ROW_ID = -2;
const COUNT_SECTION_ROW_ID = -3;

const sectionRow = (id: number, title: string): CostingBudgetRow => ({
	id,
	costingType: title,
	budget: '',
	reviewCategoryName: '',
	kind: 'section',
});

export const CostingBudgetTable: React.FC<CostingBudgetTableProps> = ({
	costingTypes,
	budgets,
	onBudgetChange,
	isLoading = false,
}) => {
	// Transform costing types into table rows
	const tableData: CostingBudgetRow[] = React.useMemo(() => {
		const rows: CostingBudgetRow[] = costingTypes.map(costingType => ({
			id: costingType.id,
			costingType: costingType.name,
			budget: budgets[costingType.id] || '0',
			reviewCategoryName: costingType.reviewCategoryName,
			kind: rowKindFor(costingType.reviewCategoryName),
		}));

		const costRows = rows.filter(row => row.kind === 'cost');
		const revenueRows = rows.filter(row => row.kind === 'revenue');
		const countRows = rows.filter(row => row.kind === 'count');

		// Total is COST ONLY. Revenue lines and the OPD headcount are not costs, so adding
		// them here produced a number that meant nothing.
		const total = costRows.reduce((sum, row) => {
			const value = parseFloat(row.budget) || 0;
			return sum + value;
		}, 0);

		return [
			...costRows,
			{
				id: TOTAL_ROW_ID,
				costingType: 'Total Cost',
				budget: total.toString(),
				reviewCategoryName: '',
				kind: 'total' as RowKind,
			},
			...(revenueRows.length
				? [sectionRow(REVENUE_SECTION_ROW_ID, 'Revenue — not part of Total Cost'), ...revenueRows]
				: []),
			...(countRows.length
				? [
						sectionRow(COUNT_SECTION_ROW_ID, 'Counts — not money, not part of Total Cost'),
						...countRows,
					]
				: []),
		];
	}, [costingTypes, budgets]);

	const columns: TableColumn<CostingBudgetRow>[] = React.useMemo(
		() => [
			{
				key: 'costingType',
				title: 'Costing Type',
				align: 'left',
				width: '300px',
				// Row order is load-bearing now (cost lines, Total, then the non-cost groups),
				// so re-sorting would scatter the section headings.
				sortable: false,
				cellClassName: 'text-xs',
				render: (_value: unknown, record: CostingBudgetRow) => {
					if (record.kind === 'section') {
						return (
							<div className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
								{record.costingType}
							</div>
						);
					}
					return (
						<div className={record.kind === 'total' ? 'font-semibold text-gray-900' : undefined}>
							{record.costingType}
						</div>
					);
				},
			},
			{
				key: 'budget',
				title: 'Budget',
				align: 'right',
				width: '200px',
				sortable: false,
				headerClassName: '[&>div]:justify-end',
				render: (_value: unknown, record: CostingBudgetRow) => {
					if (record.kind === 'section') {
						return null;
					}
					if (record.kind === 'total') {
						const totalValue = parseFloat(record.budget) || 0;
						return (
							<div className='text-sm font-semibold text-gray-900 text-right'>
								₹
								{totalValue.toLocaleString('en-IN', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}
							</div>
						);
					}
					// Money rows carry a ₹ prefix; the headcount row carries its unit instead, so
					// the two are never mistaken for each other.
					const isMoney = record.kind !== 'count';
					return (
						<div className='flex items-center justify-end space-x-1'>
							{isMoney && <span className='text-xs text-gray-500'>₹</span>}
							<input
								type='number'
								value={record.budget || '0'}
								onChange={e => onBudgetChange(record.id, e.target.value)}
								placeholder={isMoney ? 'Enter budget' : 'Enter count'}
								className='w-32 px-3 py-2 border border-gray-300 rounded-md text-sm text-right focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-200'
							/>
							{!isMoney && <span className='text-xs text-gray-500'>people</span>}
						</div>
					);
				},
			},
		],
		[onBudgetChange]
	);

	if (isLoading) {
		return (
			<div className='bg-white rounded-lg border border-gray-200 p-6'>
				<div className='text-center text-gray-500'>Loading costing types...</div>
			</div>
		);
	}

	return (
		<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
			<div className='bg-gray-50 border-b border-gray-200 px-6 py-3'>
				<h3 className='text-sm font-semibold text-gray-900'>Budget</h3>
			</div>
			<Table<CostingBudgetRow>
				data={tableData}
				columns={columns}
				size='sm'
				emptyText='No costing types found'
				rowKey={record => record.id}
			/>
		</div>
	);
};
